import {
  getBestSellerProducts,
  getPublicEvents,
  getPublicMoods,
  getPublicMoodItems,
  getPublicProduct,
  getPublicStores,
  type PublicMoodModel,
  type PublicMoodRecommendationModel,
  type PublicEventModel,
} from '@nexacore/api-clients/fnb/public';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { responseErrorCode } from '@/features/entry/entryErrors';
import type { PublicProduct, PublicStore } from '@/features/menu/types';
import { useBrowseStore } from '@/state/browseStoreStore';

export type PublicMood = PublicMoodModel;
export type PublicMoodRecommendation = PublicMoodRecommendationModel;
export type PublicEvent = PublicEventModel;

const locale = 'vi-VN';

export function useBestSellerProducts(limit = 8, scopedStoreCode?: string) {
  const selectedStoreCode = useBrowseStore((state) => state.store?.storeCode);
  const storeCode = scopedStoreCode ?? selectedStoreCode;
  return useQuery({
    queryKey: ['public-discovery-products', 'best-sellers', storeCode, limit, locale],
    queryFn: async (): Promise<{ products: PublicProduct[]; stores: PublicStore[] }> => {
      const response = await getBestSellerProducts(limit, undefined, locale, storeCode);
      const code = responseErrorCode(response);
      if (!response.succeeded || code)
        throw Object.assign(new Error(code ?? 'FNB_PUBLIC_MENU_UNAVAILABLE'), { publicCode: code });
      return {
        products: response.data?.products ?? [],
        stores: response.data?.stores ?? [],
      };
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function usePublicEvents(limit = 10) {
  const storeCode = useBrowseStore((state) => state.store?.storeCode);
  return useQuery({
    queryKey: ['public-discovery-events', storeCode, limit, locale],
    queryFn: async (): Promise<PublicEvent[]> => {
      const response = await getPublicEvents(storeCode, limit, undefined, locale);
      const code = responseErrorCode(response);
      if (!response.succeeded || code)
        throw Object.assign(new Error(code ?? 'FNB_PUBLIC_EVENT_UNAVAILABLE'), {
          publicCode: code,
        });
      return response.data?.events ?? [];
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function usePublicProduct(productId: string, storeCode?: string) {
  return useQuery({
    queryKey: ['public-discovery-product', productId, storeCode, locale],
    queryFn: async (): Promise<{ product: PublicProduct; stores: PublicStore[] }> => {
      const response = await getPublicProduct(productId, undefined, locale, storeCode);
      const code = responseErrorCode(response);
      if (!response.succeeded || code || !response.data?.product)
        throw Object.assign(new Error(code ?? 'FNB_PUBLIC_PRODUCT_NOT_FOUND'), {
          publicCode: code,
        });
      return {
        product: response.data.product,
        stores: response.data.product.stores ?? response.data.stores ?? [],
      };
    },
    enabled: Boolean(productId),
    retry: false,
    staleTime: 60_000,
  });
}

export function usePublicStores() {
  return useQuery({
    queryKey: ['public-discovery-stores', locale],
    queryFn: async (): Promise<PublicStore[]> => {
      const response = await getPublicStores(undefined, locale);
      const code = responseErrorCode(response);
      if (!response.succeeded || code)
        throw Object.assign(new Error(code ?? 'FNB_PUBLIC_STORE_INACTIVE'), { publicCode: code });
      return response.data?.stores ?? [];
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function usePublicMoods({ enabled = true }: { enabled?: boolean } = {}) {
  const storeCode = useBrowseStore((state) => state.store?.storeCode);
  return useQuery({
    queryKey: ['public-discovery-moods', storeCode, locale],
    queryFn: async (): Promise<PublicMood[]> => {
      const response = await getPublicMoods(undefined, locale, storeCode);
      const code = responseErrorCode(response);
      if (!response.succeeded || code)
        throw Object.assign(new Error(code ?? 'FNB_PUBLIC_MOOD_UNAVAILABLE'), { publicCode: code });
      return response.data?.moods ?? [];
    },
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export function usePublicMoodRecommendations(
  moodCode?: string,
  { limit = 8, enabled = true }: { limit?: number; enabled?: boolean } = {}
) {
  const storeCode = useBrowseStore((state) => state.store?.storeCode);
  return useQuery({
    queryKey: ['public-discovery-mood-recommendations', moodCode, storeCode, limit, locale],
    queryFn: async (): Promise<PublicMoodRecommendation[]> => {
      const response = await getPublicMoodItems(moodCode!, limit, undefined, locale, storeCode);
      const code = responseErrorCode(response);
      if (!response.succeeded || code)
        throw Object.assign(new Error(code ?? 'FNB_PUBLIC_MOOD_UNAVAILABLE'), {
          publicCode: code,
        });
      return response.data?.moodRecommendations ?? [];
    },
    enabled: Boolean(moodCode) && enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export function useInfinitePublicMoodRecommendations(
  moodCode?: string,
  { pageSize = 10, enabled = true }: { pageSize?: number; enabled?: boolean } = {}
) {
  const storeCode = useBrowseStore((state) => state.store?.storeCode);
  return useInfiniteQuery({
    queryKey: [
      'public-discovery-mood-recommendations',
      'paged',
      moodCode,
      storeCode,
      pageSize,
      locale,
    ],
    queryFn: async ({ pageParam }): Promise<PublicMoodRecommendation[]> => {
      const response = await getPublicMoodItems(
        moodCode!,
        pageSize,
        undefined,
        locale,
        storeCode,
        pageParam
      );
      const code = responseErrorCode(response);
      if (!response.succeeded || code)
        throw Object.assign(new Error(code ?? 'FNB_PUBLIC_MOOD_UNAVAILABLE'), {
          publicCode: code,
        });
      return response.data?.moodRecommendations ?? [];
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.length === pageSize ? lastPageParam + 1 : undefined,
    enabled: Boolean(moodCode) && enabled,
    retry: false,
    staleTime: 60_000,
  });
}
